// --- Angular
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

// --- RxJs
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';

// --- 3rd Party
import _ from 'lodash';

// --- HS Code
import {
  Constants,
  GroupModel,
  LanguageModel,
  RespondentModel,
  RespondentProgressModel,
  RespondentStatuses,
  ResultsStatuses,
  SurveyStatuses,
  SurveySummaryModel,
} from '../../../models';
import { LanguagesBaseService } from '../../../core/services';

// #endregion

@Component({
  selector: 'hs-respondents-widget',
  templateUrl: './respondents-widget.component.html',
  styleUrls: ['./respondents-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RespondentsWidgetComponent implements OnInit, OnDestroy, OnChanges {

  @Input() hasOthers: boolean = false;
  @Input() openPacksFeatureEnabled: boolean = false;
  @Input() numberOfCompletedRespondents: number | null = null;
  @Input() othersProgress: number = 0;
  @Input() respondentGroups: GroupModel[] = new Array<GroupModel>();
  @Input() respondentProgress: RespondentProgressModel | null = null;
  @Input() respondentsExpanded = false;
  @Input() selectedSurvey: SurveySummaryModel | null = null;
  @Input() surveys: Array<SurveySummaryModel> = new Array<SurveySummaryModel>();
  @Input() surveyType: number = 0; // ToDo: Do something actually useful with SurveyType or remove it
  @Input() totalNumberOfRespondents: number | null = null;

  @Output() manageRespondentsButtonClicked = new EventEmitter<string>();
  @Output() toggleRespondents = new EventEmitter<boolean>();
  @Output() sendReminder = new EventEmitter<RespondentModel>();

  groupExpandedStates = new Array<boolean>();

  allComplete: boolean = false;
  progressCaption: string | undefined;
  resultsStatuses = ResultsStatuses;
  surveyStatuses = SurveyStatuses;

  expandedIndex = 0;

  selectedLanguage: LanguageModel | undefined;
  showRemainingDaysOnRight = false;

  private _openPackFeatureEnabledSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _respondentGroupsSubject$: BehaviorSubject<GroupModel[] | null> = new BehaviorSubject<GroupModel[] | null>(null)
  private _allCompleteSubscription: Subscription = Subscription.EMPTY;
  private _languageSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private _languageService: LanguagesBaseService
  ) { }

   ngOnInit() {
     for (let i = 0; i < Constants.MAXIMUM_NUMBER_OF_RESPONDENT_GROUPS; i++) {
      this.groupExpandedStates[i] = false;
     }
     this._allCompleteSubscription = combineLatest({
        openPacksFeatureEnabled: this._openPackFeatureEnabledSubject$,
        respondentGroups: this._respondentGroupsSubject$}).subscribe(({openPacksFeatureEnabled, respondentGroups}) => {
          if (!respondentGroups) return;

          this.selectedSurvey?.numberOfRespondents

          const respondents = _.flatten(this.respondentGroups.map(rg => rg.respondents));
          this.allComplete = openPacksFeatureEnabled && respondents.every(r => r.status === RespondentStatuses.completedSurvey) && respondents.length == this.selectedSurvey?.numberOfRespondents;

          if (this.openPacksFeatureEnabled) {
            this.progressCaption = respondents.filter(r => r.status === RespondentStatuses.completedSurvey).length +
              ' / ' +
              respondents.filter(r => r.emailAddress.length > 0).length;
          }
      });
     this._languageSubscription = this._languageService.selectedLanguage$.subscribe(language => {
      this.selectedLanguage = language;
      if (this.selectedLanguage.isoTwoLetterName === Constants.TURKISH_LANGUAGE_ISO_TWO_LETTER_CODE) {
        this.showRemainingDaysOnRight = true;
      } else {
        this.showRemainingDaysOnRight = false;
      }
    });
   }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['respondentGroups'] && changes['respondentGroups'].currentValue) {
      this._respondentGroupsSubject$.next(this.respondentGroups);
    }

    if (changes && changes['openPacksFeatureEnabled'] && changes['openPacksFeatureEnabled'].currentValue) {
      this._openPackFeatureEnabledSubject$.next(this.openPacksFeatureEnabled);
    }
  }

  ngOnDestroy() {
    if (this._allCompleteSubscription !== Subscription.EMPTY) {
      this._allCompleteSubscription.unsubscribe();
      this._allCompleteSubscription = Subscription.EMPTY;
    }
    if (this._languageSubscription !== Subscription.EMPTY) {
      this._languageSubscription.unsubscribe();
      this._languageSubscription = Subscription.EMPTY;
    }
  }

  onWidgetButtonClick(): void {
    if (this.selectedSurvey) {
      this.manageRespondentsButtonClicked.emit(this.selectedSurvey.id);
    }
  }

  onToggle(expand: boolean) {
    this.toggleRespondents.emit(expand);
  }

  onSendReminder(respondent: RespondentModel) {
    this.sendReminder.emit(respondent);
  }

  onPanelCollapsed(groupIndex: number) {
    const isCollapsed = this.groupExpandedStates[groupIndex - 1];
    for (let i = 0; i < this.groupExpandedStates.length; i++) {
      this.groupExpandedStates[i] = false;
    }
    this.groupExpandedStates[groupIndex - 1] = !isCollapsed;
    this.groupExpandedStates = this.groupExpandedStates.splice(0);
  }
}

